import React, { useEffect } from "react";
import BottomDialog from "./BottomDialog.js";
import CenterDialog from "./CenterDialog.js";
import { useDimensions } from "src/utils/useDimensions.js";
import NativeCheckout from "../checkouts/NativeCheckout.js";
import { Box, Typography, Icon } from "@mui/material";
import ConsumerUpgradeContent from "src/components/upgrades/consumer/ConsumerUpgradeContent.js";
import { customEvent } from "src/utils/gtag.js";

const NativeCheckoutDialog = ({ open, onClose, calendar, ...props }) => {
  const { isSmallScreen } = useDimensions();

  useEffect(() => {
    if (open) {
      customEvent({
        name: "landed_on_native_checkout_dialog",
        category: "Upgrade",
        label: "Landed on Native Checkout Dialog",
        data: {
          calendar_name: calendar?.name,
          calendar_id: calendar?._id,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleUpgradeClick = () => {
    const dialogContent = document.querySelector(".MuiDialogContent-root");
    if (dialogContent) dialogContent.scrollTo({ top: 0, behavior: "smooth" });
  };

  const SocialProof = () => {
    if (!calendar?.name) return null;

    // Generate consistent rating and review count based on calendar ID
    const calendarSeed = calendar?._id ? calendar._id.slice(-3) : "000";
    const seedNumber = parseInt(calendarSeed, 16) || 100;
    
    // Fixed rating between 4.90-4.99 based on calendar
    const rating = (4.90 + (seedNumber % 10) / 100).toFixed(2);
    
    // Fixed review count between 200-400 based on calendar
    const reviewCount = 200 + (seedNumber % 201);

    return (
      <Box
        sx={{
          mb: 4,
          mx: "auto",
          maxWidth: "450px",
        }}
      >
        {/* Social Proof card */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2.5,
            borderRadius: "12px",
            background: "rgba(255, 255, 255, 0.7)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1), 0 1px 0 rgba(255, 255, 255, 0.5) inset",
          }}
        >
          {/* Left side - Fan Favorite with trophy icon */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                fontSize: "20px",
                color: "#222",
                fontWeight: 400,
              }}
            >
              trophy
            </Icon>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: "#222",
                lineHeight: 1.2,
              }}
            >
              {calendar.name} Fan Favorite Calendar
            </Typography>
          </Box>

          {/* Vertical divider */}
          <Box 
            sx={{ 
              width: "1px", 
              height: "40px", 
              backgroundColor: "#e0e0e0",
              mx: 2 
            }} 
          />

          {/* Rating section */}
          <Box sx={{ textAlign: "center", minWidth: "60px" }}>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: "#222",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {rating}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.25, justifyContent: "center" }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Icon
                  key={star}
                  sx={{
                    fontSize: "12px",
                    color: "#222",
                  }}
                >
                  ★
                </Icon>
              ))}
            </Box>
          </Box>

          {/* Second vertical divider - desktop only */}
          {!isSmallScreen && (
            <Box 
              sx={{ 
                width: "1px", 
                height: "40px", 
                backgroundColor: "#e0e0e0",
                mx: 2 
              }} 
            />
          )}

          {/* Reviews section - desktop only */}
          {!isSmallScreen && (
            <Box sx={{ textAlign: "center", minWidth: "60px" }}>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#222",
                  lineHeight: 1.2,
                  mb: 0.5,
                  mt: 0.5
                }}
              >
                {reviewCount}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "primary.main",
                  lineHeight: 1.2,
                }}
              >
                Reviews
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {isSmallScreen ? (
        <BottomDialog
          open={open}
          handleClose={onClose}
          sx={{ height: "90%" }}
          hideRectangle
          bubbleStyle
        >
          <SocialProof />
          <NativeCheckout key={open ? 'open' : 'closed'} {...props} />
          <ConsumerUpgradeContent handleUpgradeClick={handleUpgradeClick} />
        </BottomDialog>
      ) : (
        <CenterDialog
          open={open}
          handleClose={onClose}
          sx={{ height: "auto", ".MuiDialogContent-root": { mt: "60px" } }}
        >
          <Box
            sx={{
              width: "800px",
              maxWidth: "100%",
              minHeight: "380px",
              maxHeight: "600px",
            }}
          >
            <SocialProof />
            <NativeCheckout key={open ? 'open' : 'closed'} {...props} />
            <ConsumerUpgradeContent handleUpgradeClick={handleUpgradeClick} />
          </Box>
        </CenterDialog>
      )}
    </>
  );
};

export default NativeCheckoutDialog;
